import React from "react";
import {
  Grid,
  TextField,
  Container,
  Button,
  Paper,
  CssBaseline,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  InputAdornment
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { toast } from "react-toastify";
import CurrencyInput from "../common/CurrencyInput";
import * as premiumUtils from "./PremiumUtils";
import * as companyApi from "../../api/companyApi";

export default class NewPremiumAdmin extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      premium: {},
      allPremiums: [],
      prizeId: -1,
      ownerships: [],
      newOwnership: {},
      ownership: {},
      membersbyOrg: [],
      membership: "TFF",
      openNew: false,
      openEdit: false,
      error: {},
      errorNew: {},
      errorEdit: {},
      tffCodes: [],
      selectedTffCode: {},
    };
  }

  async componentDidMount() {
    try {
      const lastYear = new Date().getFullYear() - 1;
      this.setState({
        premium: { ...this.state.premium, prizeYear: lastYear },
      });
      this.fetchMembersByOrg();
    } catch (error) {
      console.error("Error in componentDidMount:", error);
    }
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.membership !== this.state.membership) {
      this.setState(
        {
          premium: { orgMemId: "", prizeYear: new Date().getFullYear() - 1, amount: "" },
          error: {},
          membersbyOrg: [],
          allPremiums: [],
        },
        this.fetchMembersByOrg
      );
    }
  }

  fetchMembersByOrg = async () => {
    try {
      const members = await premiumUtils.fetchMembersByOrg(this.state.membership);
      this.setState({ membersbyOrg: members });
    } catch (error) {
      console.error("Error in fetchMembersByOrg:", error);
    }
  };

  fetchExistingPremium = async () => {
    try {
      const { membership, premium } = this.state;

      const selectedMember = this.state.membersbyOrg.find(
        (member) => member.orgMemId === this.state.premium.orgMemId
      );

      if (!selectedMember) {
        console.error("No matching member found for the selected orgMemId.");
        return;
      }

      const result = await premiumUtils.fetchExistingPremium(
        membership,
        premium.prizeYear,
        selectedMember.companyId,
        membership === "TFF" ? premium.reportedCodeTff : 0
      );

      if (result) {
        let { existingPremium, prizeId, existingAmount, ownerships, allPremiums } = result;

        const mainTffCode = this.state.tffCodes.find((c) => c.mainCode)?.code;
        if (this.state.membership === "TFF" && mainTffCode && Array.isArray(allPremiums)) {
          const updatedList = [...allPremiums];

          const legacyIndex = updatedList.findIndex(p => p.codeTFF === 0);
          const mainIndex = updatedList.findIndex(p => Number(p.codeTFF) === Number(mainTffCode));

          if (legacyIndex !== -1) {
            const legacy = updatedList[legacyIndex];
            const legacyAmount = Number(legacy.amount || 0);

            if (mainIndex !== -1) {
              updatedList[mainIndex] = {
                ...updatedList[mainIndex],
                amount: Number(updatedList[mainIndex].amount || 0) + legacyAmount,
              };
            } else {
              updatedList.push({ ...legacy, codeTFF: mainTffCode });
            }

            updatedList.splice(legacyIndex, 1);
            allPremiums = updatedList;

            if (!existingPremium) {
              const mergedPremium = updatedList.find(p => Number(p.codeTFF) === Number(mainTffCode));
              if (mergedPremium) {
                existingPremium = mergedPremium;
                existingAmount = mergedPremium.amount;
                prizeId = mergedPremium.prizeId;
                this.setState(prev => ({
                  premium: {
                    ...prev.premium,
                    reportedCodeTff: mainTffCode
                  }
                }));
              }
            }
          }
        }

        this.setState({
          premium: {
            ...premium,
            amount: existingAmount && existingAmount !== null !== undefined ? existingAmount : "",
            existingPremium: !!existingPremium,
          },
          ownerships: ownerships || [],
          prizeId: prizeId || -1,
          allPremiums: allPremiums || [],
        });
      }
    } catch (error) {
      console.error("Error in fetchExistingPremium:", error);
    }
  };

  onChange = async (e) => {
    const { name, value } = e.target;
    const updatedValue = name === "prizeYear" ? parseInt(value, 10) : value;
    const { allPremiums, membersbyOrg, membership } = this.state;

    const updatedPremium = {
      ...this.state.premium,
      [name]: updatedValue,
    };

    if (name === "reportedCodeTff") {
      const matchingPremium = allPremiums.find(
        (p) => Number(p.codeTFF) === Number(value)
      );

      if (matchingPremium) {
        updatedPremium.amount = matchingPremium.amount;
        updatedPremium.prizeId = matchingPremium.prizeId;
        updatedPremium.existingPremium = true;
      } else {
        updatedPremium.amount = "";
        updatedPremium.prizeId = -1;
        updatedPremium.existingPremium = false;
      }
    }

    this.setState(
      {
        error: "",
        premium: updatedPremium,
      },
      async () => {
        if (name === "orgMemId" || name === "prizeYear") {
          await this.fetchExistingPremium();

          // If TFF + org change: update company + tffCodes + mainCode
          if (name === "orgMemId" && membership === "TFF") {
            const selectedMember = membersbyOrg.find(
              (member) => member.orgMemId === value
            );
            if (selectedMember) {
              const company = await companyApi.getCompany(selectedMember.companyId);
              if (company?.codeTFF) {
                const mainCode = company.codeTFF.find((c) => c.mainCode);
                const codeToUse = mainCode?.code || company.codeTFF[0]?.code || "";

                await this.setState({
                  tffCodes: company.codeTFF,
                  premium: {
                    ...this.state.premium,
                    reportedCodeTff: codeToUse,
                  }
                });

                await this.fetchExistingPremium();

                const matchingPremium = this.state.allPremiums.find(
                  (p) => Number(p.codeTFF) === Number(codeToUse)
                );

                this.setState((prevState) => ({
                  tffCodes: company.codeTFF,
                  premium: {
                    ...prevState.premium,
                    reportedCodeTff: codeToUse,
                    amount: matchingPremium?.amount ?? "",
                    prizeId: matchingPremium?.prizeId ?? -1,
                    existingPremium: !!matchingPremium,
                  },
                }));
              }
            }
          }
        }
      }
    );
  };

  onSave = async (event) => {
    event.preventDefault();

    const { valid, error, newAmount } = premiumUtils.formIsValid(
      this.state.premium,
      "new");
    this.setState({ error });

    if (!valid) return;

    // eslint-disable-next-line react/no-direct-mutation-state
    this.state.premium.amount = newAmount;

    const premium = {
      ...this.state.premium,
      reportedCodeTff: parseInt(this.state.premium.reportedCodeTff, 10),
      amount: newAmount,
    };

    const savedPremium = await premiumUtils.savePremium(
      premium,
      "new");
    if (savedPremium) {
      this.setState({
        ownerships: savedPremium.ownerships,
        prizeId: savedPremium.prizeId
      });
      await this.fetchExistingPremium();
    } else {
      console.error("Save operation failed.");
    }
  };

  toggleDialog = (dialogType, isOpen, data = null) => {
    this.setState(premiumUtils.toggleDialog(dialogType, isOpen, data));
  };

  handleInputChange = (field, value, ownershipType = "newOwnership", errorField = null) => {
    this.setState(premiumUtils.handleInputChange(field, value, ownershipType, errorField, this.state));
  };

  handleSaveDialog = async (ownershipType = "newOwnership") => {
    const result = await premiumUtils.handleSaveDialog(ownershipType, this.state);
    if (!result.success) {
      this.setState({
        errorNew: result.errorNew || {},
        errorEdit: result.errorEdit || {},
      });
      toast.error("Det skjedd noe feil...");
    } else {
      const { fetchOwnerships, newOwnership, openNew, openEdit } = result;

      this.setState({
        ownerships: fetchOwnerships || this.state.ownerships,
        newOwnership: newOwnership || this.state.newOwnership,
        openNew: openNew !== undefined ? openNew : this.state.openNew,
        openEdit: openEdit !== undefined ? openEdit : this.state.openEdit,
      });

      toast.success(`Eierskap er ${ownershipType === "newOwnership" ? "opprettet" : "oppdatert"}`);
    }
  };

  handleTransferPremium = async (prizeId) => {
    const targetCode = this.state.selectedTffCode[prizeId];
    if (!targetCode) {
      console.warn("Ingen TFF-kode valgt for overføring.");
      return;
    }

    const success = await premiumUtils.transferPremiumToCodeAndHandle(prizeId, this.state.premium.prizeYear, targetCode);

    if (success) {
      await this.fetchExistingPremium();
    }
  };

  render() {
    const dateFormat = (date) => {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    };

    const root = { width: "100%" };
    const layout = { width: "100%", paddingTop: 80 };
    const paper = { paddingLeft: 30, paddingTop: 20, paddingRight: 30, paddingBottom: 50 };
    const gridPaddingTop = { paddingTop: 20 };
    const head = { backgroundColor: "#eeeeee", fontWeight: "bold", fontSize: 16, textDecoration: "none" };
    const formcontrolselect = { minWidth: "100%" };
    const buttonGroupStyles = { container: { marginTop: "10px" }, button: { margin: "5px" } };
    const NOKPrefix = <span style={{ fontSize: "16px", marginRight: "5px" }}>NOK</span>;

    return (
      <Container maxWidth="lg">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <form onSubmit={this.onSave}>
            <main style={layout}>
              <Paper style={paper}>
                <React.Fragment>
                  <CssBaseline />
                  <Grid container spacing={4}>
                    {/* Premium Input Section */}
                    <Grid item lg={5}>
                      <h2>{this.state.premium.existingPremium ? "Oppdater" : "Ny"} Premie</h2>
                      <Grid container justifyContent="flex-start" style={buttonGroupStyles.container}>
                        <FormControl component="fieldset">
                          <RadioGroup
                            row
                            value={this.state.membership}
                            onChange={(e) => this.setState({ membership: e.target.value })}
                          >
                            <FormControlLabel
                              value="TFF"
                              control={<Radio color="primary" />}
                              label="TFF"
                            />
                            <FormControlLabel
                              value="YFF"
                              control={<Radio color="primary" />}
                              label="YFF"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                      <Grid>
                        <FormControl fullWidth variant="standard" style={formcontrolselect}>
                          <InputLabel>Medlem</InputLabel>
                          <Select
                            name="orgMemId"
                            onChange={this.onChange}
                            variant="standard"
                            value={this.state.premium.orgMemId || ""}
                          >
                            {this.state.membersbyOrg.map((member, index) => (
                              <MenuItem key={index} value={member.orgMemId}>
                                {`${member.companyName} (${member.organisationName})`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Typography style={{ color: "red" }}>
                        {this.state.error.orgMemId}
                      </Typography>
                      {this.state.membership === "TFF" && (
                        <Grid style={gridPaddingTop}>
                          <FormControl style={formcontrolselect} fullWidth>
                            {this.state.tffCodes.length === 1 ? (
                              <TextField
                                name="reportedCodeTff"
                                label="TFF Kode"
                                value={this.state.tffCodes[0].code}
                                fullWidth
                                variant="standard"
                                InputProps={{ readOnly: true }}
                              />
                            ) : (
                              <TextField
                                select
                                name="reportedCodeTff"
                                label="TFF Kode"
                                value={this.state.premium.reportedCodeTff || ""}
                                onChange={this.onChange}
                                fullWidth
                                variant="standard"
                                SelectProps={{ native: true }}
                              >
                                <option value="" disabled></option>
                                {this.state.tffCodes.map((codeObj) => (
                                  <option key={codeObj.code} value={codeObj.code}>
                                    {codeObj.code} {codeObj.mainCode ? "(Hovedkode)" : ""}
                                  </option>
                                ))}
                              </TextField>
                            )}
                          </FormControl>
                          {(() => {
                            const mainCodeCount = this.state.tffCodes.filter(c => c.mainCode).length;

                            if (mainCodeCount > 1) {
                              return (
                                <Typography variant="body2" color="error" style={{ paddingTop: 8 }}>
                                  Mer enn én hovedkode registrert, handling kreves
                                </Typography>
                              );
                            }

                            if (mainCodeCount === 0 && this.state.tffCodes.length > 0) {
                              return (
                                <Typography variant="body2" color="error" style={{ paddingTop: 8 }}>
                                  Ingen hovedkode registrert, handling kreves
                                </Typography>
                              );
                            }

                            return null;
                          })()}
                          <FormHelperText>Velg TFF kode å registrere premie for</FormHelperText>
                        </Grid>
                      )}
                      <Grid style={gridPaddingTop}>
                        <TextField
                          name="prizeYear"
                          label="År"
                          type="number"
                          variant="standard"
                          value={this.state.premium.prizeYear || ""}
                          fullWidth
                          onChange={this.onChange}
                        />
                      </Grid>
                      <Typography style={{ color: "red" }}>
                        {this.state.error.prizeYear}
                      </Typography>
                      <Grid item style={gridPaddingTop}>
                        <div>
                          <label>
                            Opptjent premie
                          </label>
                          <div>
                            <CurrencyInput
                              name="amount"
                              InputProps={{
                                startAdornment: <InputAdornment position="start">{NOKPrefix}</InputAdornment>
                              }}
                              className="MuiInputBase-input MuiInput-input"
                              type="text"
                              includeThousandsSeparator={true}
                              allowDecimal={false}
                              integerLimit={20}
                              decimalLimit={0}
                              value={
                                this.state.premium.amount !== null && this.state.premium.amount !== undefined
                                  ? String(this.state.premium.amount)
                                  : ""
                              }
                              onChange={this.onChange}
                            />
                          </div>
                          <FormHelperText>
                            {`Opptjent premie i ${this.state.premium.prizeYear || "[År ikke valgt]"} for lovpålagt 
                          ${this.state.membership === "TFF" ? "trafikkforsikring" : "yrkesskadeforsikring"} (i hele norske kroner). 
                          Direkte norsk forretning uten fradrag av reassuranse${this.state.membership === "TFF" ? " og uten TFA" : ""}.`}
                          </FormHelperText>
                        </div>
                      </Grid>
                      <Typography style={{ color: "red" }}>
                        {this.state.error.amount}
                      </Typography>
                      <Grid style={{ paddingTop: 40 }}>
                        <Button
                          startIcon={<SaveIcon />}
                          type="submit"
                          variant="contained"
                          color="primary"
                        >
                          {"Lagre"}
                        </Button>
                      </Grid>
                    </Grid>
                    {/* Ownership Section */}
                    <Grid item lg={7}>
                      <h2>Opprinnelse og eierskap</h2>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => this.toggleDialog("openNew", true)}
                        startIcon={<AddIcon />}
                      >
                        Ny
                      </Button>
                      <Dialog
                        open={this.state.openNew}
                        onClose={() => this.toggleDialog("openNew", false)}
                        fullWidth
                      >
                        <DialogTitle>Ny eierskap</DialogTitle>
                        <DialogContent>
                          <Grid>
                            <FormControl style={formcontrolselect}>
                              <InputLabel>Eier</InputLabel>
                              <Select
                                name="ownedByOrgMemId"
                                value={this.state.newOwnership.ownedByOrgMemId || ""}
                                onChange={(e) =>
                                  this.handleInputChange("ownedByOrgMemId", e.target.value, "newOwnership", "errorNew")
                                }
                                error={this.state.errorNew.ownedByOrgMemId}
                              >
                                {this.state.membersbyOrg.map((member, index) => (
                                  <MenuItem key={index} value={member.orgMemId}>
                                    {`${member.companyName} (${member.organisationName})`}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid>
                            <DatePicker
                              label="Fra"
                              format="dd.MM.yyyy"
                              value={this.state.newOwnership.fromDate || null}
                              onChange={(date) =>
                                this.handleInputChange("fromDate", date, "newOwnership", "errorNew")
                              }
                              slotProps={{ textField: { error: !!this.state.errorNew.fromDate } }}
                            />
                          </Grid>
                          <Grid>
                            <DatePicker
                              label="Til"
                              format="dd.MM.yyyy"
                              value={this.state.newOwnership.toDate || null}
                              onChange={(date) =>
                                this.handleInputChange("toDate", date, "newOwnership", "errorNew")
                              }
                              slotProps={{ textField: { error: !!this.state.errorNew.toDate } }}
                            />
                          </Grid>
                          <Typography style={{ color: "red" }}>
                            {this.state.errorNew.toDate}
                          </Typography>
                        </DialogContent>
                        <DialogActions>
                          <Button
                            onClick={() => this.toggleDialog("openNew", false)}
                            color="primary"
                          >
                            Avbryt
                          </Button>
                          <Button
                            onClick={() => this.handleSaveDialog("newOwnership")}
                            color="primary"
                          >
                            Lagre
                          </Button>
                        </DialogActions>
                      </Dialog>
                      <Paper style={root}>
                        <Table stickyHeader aria-label="sticky table">
                          <TableHead>
                            <TableRow>
                              <TableCell style={head}>Rediger</TableCell>
                              <TableCell style={head}>Navn</TableCell>
                              <TableCell style={head}>Fra</TableCell>
                              <TableCell style={head}>Til</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {this.state.ownerships.length > 0 ? (
                              this.state.ownerships.map((ownership, index) => {
                                const fromDate = dateFormat(ownership?.fromDate);
                                const toDate = dateFormat(ownership?.toDate);
                                return (
                                  <TableRow hover tabIndex={-1} key={index}>
                                    <TableCell>
                                      <Button
                                        variant="outlined"
                                        onClick={() =>
                                          this.toggleDialog("openEdit", true, ownership)
                                        }
                                        startIcon={<EditIcon />}
                                      >
                                        Rediger
                                      </Button>
                                      <Dialog
                                        open={this.state.openEdit}
                                        onClose={() => this.toggleDialog("openEdit", false)}
                                        fullWidth
                                      >
                                        <DialogTitle>Rediger eierskap</DialogTitle>
                                        <DialogContent>
                                          <Grid>
                                            <TextField
                                              autoFocus
                                              margin="dense"
                                              id="name"
                                              label="Eier"
                                              value={this.state.ownership.ownedByCompanyName || ""}
                                              fullWidth
                                              disabled
                                            />
                                          </Grid>
                                          <Grid>
                                            <DatePicker
                                              label="Fra"
                                              value={this.state.ownership?.fromDate || null}
                                              onChange={(date) =>
                                                this.handleInputChange("fromDate", date, "ownership", "errorEdit")
                                              }
                                              renderInput={(params) => (
                                                <TextField {...params} error={!!this.state.errorEdit.fromDate} helperText={this.state.errorEdit.fromDate} />
                                              )}
                                            />
                                          </Grid>
                                          <Grid>
                                            <DatePicker
                                              label="Til"
                                              value={this.state.ownership?.toDate || null}
                                              onChange={(date) =>
                                                this.handleInputChange("toDate", date, "ownership", "errorEdit")
                                              }
                                              renderInput={(params) => (
                                                <TextField {...params} error={!!this.state.errorEdit.toDate} helperText={this.state.errorEdit.toDate} />
                                              )}
                                            />
                                          </Grid>

                                          <Typography style={{ color: "red" }}>
                                            {this.state.errorEdit.toDate}
                                          </Typography>
                                        </DialogContent>
                                        <DialogActions>
                                          <Button
                                            onClick={() => this.toggleDialog("openEdit", false)}
                                            color="primary"
                                          >
                                            Avbryt
                                          </Button>
                                          <Button
                                            onClick={() => this.handleSaveDialog("ownership")}
                                            color="primary"
                                          >
                                            Lagre
                                          </Button>
                                        </DialogActions>
                                      </Dialog>
                                    </TableCell>
                                    <TableCell>{ownership.ownedByCompanyName}</TableCell>
                                    <TableCell>{fromDate}</TableCell>
                                    <TableCell>{toDate}</TableCell>
                                  </TableRow>
                                );
                              })
                            ) : (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  Ingen eierskap
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Paper>
                    </Grid>

                    {this.state.membership === "TFF" && (
                      <Grid item xs={12} style={gridPaddingTop}>
                        <h2>Tidligere registrerte premier</h2>
                        <Paper style={root}>
                          <Table stickyHeader aria-label="registered premium table">
                            <TableHead>
                              <TableRow>
                                <TableCell style={head}>Rapportert av</TableCell>
                                <TableCell style={head}>TFF Kode</TableCell>
                                <TableCell style={head}>Handling</TableCell>
                                <TableCell style={head}>År</TableCell>
                                <TableCell style={head}>Beløp (NOK)</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {this.state.allPremiums && this.state.allPremiums.length > 0 ? (

                                <>
                                  {/* Total row */}
                                  <TableRow>
                                    <TableCell style={{ fontWeight: "bold" }}>
                                      Total sum registrert for Risikobærer
                                    </TableCell>
                                    <TableCell style={{ fontWeight: "bold" }}>
                                      -----
                                    </TableCell>
                                    <TableCell style={{ fontWeight: "bold" }}>
                                      -----
                                    </TableCell>
                                    <TableCell style={{ fontWeight: "bold" }}>
                                      {this.state.premium.prizeYear}
                                    </TableCell>
                                    <TableCell style={{ fontWeight: "bold" }}>
                                      {this.state.allPremiums
                                        .filter(p => typeof p.amount === "number" && p.amount >= 0)
                                        .reduce((sum, p) => sum + p.amount, 0)
                                        .toLocaleString("nb-NO")}
                                    </TableCell>
                                  </TableRow>

                                  {/* Individual premium rows */}
                                  {[...this.state.allPremiums]
                                    .sort((a, b) => Number(a.codeTFF) - Number(b.codeTFF))
                                    .map((p) => {
                                      const availableCodes = this.state.tffCodes.filter(
                                        (codeObj) => Number(codeObj.code) !== Number(p.codeTFF)
                                      );

                                      return (
                                        <TableRow key={p.prizeId}>
                                          <TableCell>{p.reportedByCompanyName}</TableCell>
                                          <TableCell>
                                            {p.codeTFF}
                                            {this.state.tffCodes.find(
                                              (c) => Number(c.code) === Number(p.codeTFF) && c.mainCode
                                            )
                                              ? " (Hovedkode)"
                                              : ""}
                                          </TableCell>
                                          <TableCell>
                                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                              <Select
                                                value={this.state.selectedTffCode[p.prizeId] ?? ""}
                                                onChange={(e) => {
                                                  const selectedValue = e.target.value;
                                                  this.setState((prev) => ({
                                                    selectedTffCode: {
                                                      ...prev.selectedTffCode,
                                                      [p.prizeId]: selectedValue,
                                                    },
                                                  }));
                                                }}
                                                displayEmpty
                                                variant="standard"
                                                native
                                                disabled={availableCodes.length === 0}
                                                style={{ minWidth: "100px" }}
                                              >
                                                <option value="">Velg kode</option>
                                                {this.state.tffCodes
                                                  .filter((codeObj) => Number(codeObj.code) !== Number(p.codeTFF))
                                                  .map((codeObj) => (
                                                    <option key={codeObj.code} value={codeObj.code}>
                                                      {codeObj.code} {codeObj.mainCode ? "(Hovedkode)" : ""}
                                                    </option>
                                                  ))}
                                              </Select>
                                              <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() => this.handleTransferPremium(p.prizeId)}
                                                disabled={
                                                  availableCodes.length === 0 || !this.state.selectedTffCode[p.prizeId]
                                                }
                                              >
                                                Overfør
                                              </Button>
                                            </div>
                                          </TableCell>
                                          <TableCell>{p.prizeYear}</TableCell>
                                          <TableCell>{Number(p.amount).toLocaleString("nb-NO")}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                </>
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">
                                    Ingen registrerte premier for denne perioden
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </Paper>
                      </Grid>
                    )}

                  </Grid>
                </React.Fragment>
              </Paper>
            </main>
          </form>
        </LocalizationProvider>
      </Container>
    );
  }
}
